/**
 * Auth compatibility layer for the Auth0 → Better Auth migration.
 *
 * Exports `useAuth0` / `withAuthenticationRequired` with the exact call
 * surface the app already uses, flag-switched between the real
 * @auth0/auth0-react implementations and a Better Auth (cookie session)
 * backed shim. Call sites only change their import path.
 *
 * Better Auth mode mappings:
 *   isAuthenticated / isLoading / user  → authClient.useSession()
 *   user.sub                            → session user.legacySub
 *                                         (`oauth2|<provider>|<accountId>`,
 *                                         stamped server-side; keeps
 *                                         authUtils.getAuthUserId working)
 *   loginWithRedirect({connection})     → signIn.social({provider}); without
 *                                         a connection a provider picker
 *                                         modal opens (Auth0 hosted-page
 *                                         equivalent)
 *   logout({returnTo})                  → signOut() + redirect
 *   getAccessTokenSilently              → placeholder string; the session
 *                                         cookie is the credential and the
 *                                         backend shim rewrites the header
 */
import { useEffect, useMemo } from 'react';
import {
  useAuth0 as useAuth0FromAuth0,
  withAuthenticationRequired as withAuthenticationRequiredFromAuth0,
} from '@auth0/auth0-react';
// resolved via package.json "exports" subpath, which eslint-plugin-import's
// node resolver can't read
// eslint-disable-next-line import/no-unresolved
import { createAuthClient } from 'better-auth/react';
import { BETTER_AUTH_ENABLED } from './betterAuthFlag';
import { API_BASE_URL } from '../ducks/initializers';

// Same claim namespace useTwitchUsernameFromAuth0 reads.
const NAMESPACE = 'https://rngroyale-namespace/';

export const authClient = BETTER_AUTH_ENABLED
  ? createAuthClient({
      baseURL: API_BASE_URL.replace(/\/api$/, ''),
      fetchOptions: { credentials: 'include' },
    })
  : null;

const currentPath = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash || ''}`;

// ---- provider picker (replaces the Auth0 hosted login page) ----
let pickerListeners = [];
let pendingReturnTo = '/';

const emitPicker = (open) => {
  pickerListeners.forEach((fn) => fn(open));
};

export const subscribeProviderPicker = (listener) => {
  pickerListeners.push(listener);
  return () => {
    pickerListeners = pickerListeners.filter((fn) => fn !== listener);
  };
};

export const closeProviderPicker = () => emitPicker(false);

export const chooseProvider = (provider) => {
  emitPicker(false);
  return authClient.signIn.social({
    provider,
    callbackURL: pendingReturnTo || '/',
  });
};

const loginWithRedirectCompat = (options = {}) => {
  const returnTo = options?.appState?.returnTo || currentPath();
  if (options?.connection) {
    return authClient.signIn.social({
      provider: options.connection,
      callbackURL: returnTo,
    });
  }
  pendingReturnTo = returnTo;
  emitPicker(true);
  return Promise.resolve();
};

// Link an additional provider to the CURRENT session user (redirect flow).
export const linkSocialAccount = (provider) =>
  authClient.linkSocial({ provider, callbackURL: currentPath() });

// Same redirect flow, plus the read scope the backend needs to ask YouTube
// which channel this person owns (a Google display name is not a channel).
export const YOUTUBE_READONLY_SCOPE =
  'https://www.googleapis.com/auth/youtube.readonly';
export const linkYouTubeAccount = (callbackURL) =>
  authClient.linkSocial({
    provider: 'google',
    scopes: [YOUTUBE_READONLY_SCOPE],
    callbackURL: callbackURL || currentPath(),
  });

// Same redirect flow, plus the scope the backend needs to add RNGRoyaleOfficial
// as a moderator in the viewer's own Twitch channel.
export const TWITCH_MOD_SCOPE = 'channel:manage:moderators';
export const linkTwitchModScope = (callbackURL) =>
  authClient.linkSocial({
    provider: 'twitch',
    scopes: [TWITCH_MOD_SCOPE],
    callbackURL: callbackURL || currentPath(),
  });

const logoutCompat = async (options = {}) => {
  try {
    await authClient.signOut();
  } finally {
    window.location.assign(options?.returnTo || window.location.origin);
  }
};

// The cookie session is the credential; callers only need a non-empty string
// to put in an Authorization header the backend shim overwrites.
const getAccessTokenSilentlyCompat = async () => 'better-auth-cookie-session';

const useBetterAuthCompat = () => {
  const { data, isPending } = authClient.useSession();
  const sessionUser = data?.user;

  const user = useMemo(() => {
    if (!sessionUser) return undefined;
    const name = sessionUser.name || '';
    return {
      sub: sessionUser.legacySub || '',
      name,
      nickname: name,
      email: sessionUser.email,
      picture: sessionUser.image,
      [`${NAMESPACE}username`]: name,
    };
  }, [sessionUser]);

  return {
    isAuthenticated: Boolean(sessionUser),
    isLoading: isPending,
    user,
    loginWithRedirect: loginWithRedirectCompat,
    loginWithPopup: (options = {}) => loginWithRedirectCompat(options),
    logout: logoutCompat,
    getAccessTokenSilently: getAccessTokenSilentlyCompat,
    getIdTokenClaims: async () => (user ? { name: user.name } : null),
  };
};

const withAuthenticationRequiredCompat = (Component, options = {}) => {
  const WithAuthenticationRequired = (props) => {
    const { isAuthenticated, isLoading } = useBetterAuthCompat();
    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        loginWithRedirectCompat({ appState: { returnTo: currentPath() } });
      }
    }, [isAuthenticated, isLoading]);
    if (!isAuthenticated) {
      return options.onRedirecting ? options.onRedirecting() : null;
    }
    return <Component {...props} />;
  };
  return WithAuthenticationRequired;
};

export const useAuth0 = BETTER_AUTH_ENABLED ? useBetterAuthCompat : useAuth0FromAuth0;

export const withAuthenticationRequired = BETTER_AUTH_ENABLED
  ? withAuthenticationRequiredCompat
  : withAuthenticationRequiredFromAuth0;
